'use client'

import { useEffect, useMemo, useRef, useState } from 'react'
import { usePathname } from 'next/navigation'
import { CircleCheckBig, CircleHelp, CircleX, ImagePlus, LogIn, Medal, PenLine, Send, Star, Trash, X } from 'lucide-react'
import Avatar from 'boring-avatars'
import { formatDateNormal, formatImageURL, formatNumber, isLoyaltyActive } from '@/utils/util'
import { navigateUrl, sendClientRequest } from '@/utils/client-request'
import { useCart, useProductReviews } from '@/lib/commerce/hooks'
import TextEditorMininal from '@/components/elements/TextEditorMinimal'

function Stars({ value, size = 12, interactive, onChange }: { value: number, size?: number, interactive?: boolean, onChange?: (v: number) => void }) {
  return (
    <span className="inline-flex items-center gap-0.5" style={{ color: '#fcd34d' }}>
      {[1, 2, 3, 4, 5].map(i => (
        <Star
          key={i}
          width={size}
          height={size}
          fill={i <= Math.round(value) ? 'currentColor' : 'none'}
          stroke="currentColor"
          strokeWidth={1.5}
          onClick={interactive ? () => onChange?.(i) : undefined}
          className={interactive ? 'cursor-pointer transition-transform hover:scale-110' : ''}
        />
      ))}
    </span>
  )
}

interface ReviewProps {
  product: any
  vendor?: any
  reviews?: {
    counts?: { rating: number, count: number }[]
    data?: any[]
    items?: any[]
    totalCount?: number
    average?: number
    userReviews?: any[]
  }
  canReview?: boolean
}

export default function Review({ product, vendor, reviews, canReview = false }: ReviewProps) {
  if (!product) return null

  const pathname = usePathname()
  const fileInputRef = useRef<HTMLInputElement>(null)
  const { loyaltySummary } = useCart()
  const [publicBasePoints, setPublicBasePoints] = useState(0)
  const reviewRewardPoints = isLoyaltyActive(vendor) ? (loyaltySummary?.currentTier?.reviewRewardPoints || 0) : 0
  const reviewRewardCapCount = loyaltySummary?.reviewRewardCapCount || 0
  const reviewRewardCapWindowDays = loyaltySummary?.reviewRewardCapWindowDays || 0
  const reviewRewardCapped = reviewRewardPoints > 0 && reviewRewardCapCount > 0 && reviewRewardCapWindowDays > 0 && (loyaltySummary?.reviewRewardUsedCount || 0) >= reviewRewardCapCount

  useEffect(() => {
    if (canReview || !isLoyaltyActive(vendor)) return
    let cancelled = false
    sendClientRequest({ url: '/loyalty/tiers', auth: false, cache: true, headers: { Vendor: vendor?.id } }).then(({ data }: any) => {
      if (cancelled || !data?.tiers?.length) return
      const sorted = [...data.tiers].sort((a: any, b: any) => a.minAnnualSpend - b.minAnnualSpend)
      setPublicBasePoints(sorted[0]?.reviewRewardPoints || 0)
    }).catch(() => { })
    return () => { cancelled = true }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canReview, vendor?.id])

  const reviewPointsChip = reviewRewardPoints > 0 && (
    reviewRewardCapped ? (
      <span className="inline-flex items-center gap-1 text-[11px] font-semibold px-2.5 py-0.5 rounded-full whitespace-nowrap tabular-nums" style={{ background: 'rgba(255,255,255,0.06)', color: 'rgba(255,255,255,0.55)', border: '1px solid rgba(255,255,255,0.13)' }}>
        <Medal className="h-3.5 w-3.5" />
        Онооны лимит хүрсэн
      </span>
    ) : (
      <span className="inline-flex items-center gap-1 text-[11px] font-semibold px-2.5 py-0.5 rounded-full whitespace-nowrap tabular-nums" style={{ background: 'rgba(168,85,247,0.18)', color: '#d8b4fe', border: '1px solid rgba(168,85,247,0.3)' }}>
        <Medal className="h-3.5 w-3.5" />
        +{formatNumber(reviewRewardPoints)} оноо
      </span>
    )
  )

  const {
    deletedReview, pendingReview, publishedReview, hasAnyReview,
    content, setContent,
    rating, setRating,
    images,
    btnLoading,
    loadingImages,
    postReview,
    uploadImages,
    removeImage,
  } = useProductReviews({ product, initialReviews: reviews as any })

  const [composerOpen, setComposerOpen] = useState(false)
  const [showAll, setShowAll] = useState(false)

  const totalCount = reviews?.totalCount || 0
  const average = reviews?.average || 0
  const reviewItems: any[] = reviews?.data || reviews?.items || []

  const breakdown = useMemo(() => {
    if (reviews?.counts && reviews.counts.length > 0) {
      const total = reviews.counts.reduce((s, c) => s + c.count, 0) || 1
      return [5, 4, 3, 2, 1].map(stars => {
        const found = reviews.counts!.find(c => c.rating === stars)
        const n = found?.count || 0
        return { stars, pct: Math.round((n / total) * 100), n }
      })
    }
    const counts = [5, 4, 3, 2, 1].map(stars => ({
      stars,
      n: reviewItems.filter((r: any) => Math.round(r?.rating || 0) === stars).length,
    }))
    const total = counts.reduce((s, c) => s + c.n, 0) || 1
    return counts.map(c => ({ stars: c.stars, pct: Math.round((c.n / total) * 100), n: c.n }))
  }, [reviews?.counts, reviewItems])

  const visible = showAll ? reviewItems : reviewItems.slice(0, 3)
  const hasMore = reviewItems.length > 3

  const handleImages = async (e: any) => {
    await uploadImages(e.target.files)
    if (fileInputRef.current) fileInputRef.current.value = ''
  }

  const composer = (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <span className="text-[11px] font-semibold uppercase tracking-[0.18em]" style={{ color: 'rgba(255,255,255,0.7)' }}>Үнэлгээ</span>
          <Stars value={rating} size={20} interactive onChange={setRating} />
        </div>
        <input type="file" accept="image/*" multiple ref={fileInputRef} className="hidden" onChange={handleImages} />
        <button
          type="button"
          onClick={() => fileInputRef.current?.click()}
          disabled={loadingImages}
          className="inline-flex items-center gap-2 h-9 px-3.5 rounded-full text-[12px] font-medium cursor-pointer transition-colors disabled:opacity-50"
          style={{ background: 'rgba(0,0,0,0.3)', color: 'white', border: '1px solid rgba(255,255,255,0.13)' }}
        >
          <ImagePlus className="h-3.5 w-3.5" />
          {loadingImages ? 'Уншиж байна' : 'Зураг'}
        </button>
      </div>

      <div
        className="rounded-xl px-4 py-3 min-h-32 [&_.ProseMirror]:min-h-24 [&_.ProseMirror]:outline-none [&_.ProseMirror]:text-white"
        style={{ background: 'rgba(0,0,0,0.3)', border: '1px solid rgba(255,255,255,0.13)' }}
      >
        <TextEditorMininal content={content} setContent={setContent} />
      </div>

      {images.length > 0 && (
        <div className="grid grid-cols-3 gap-2">
          {images.map((image, index) => (
            <div key={index} className="relative aspect-square">
              <img src={formatImageURL(image)} alt={`Review ${index + 1}`} className="w-full h-full object-cover rounded-md" style={{ border: '1px solid rgba(255,255,255,0.13)' }} />
              <button
                type="button"
                onClick={() => removeImage(image)}
                className="absolute top-1 right-1 h-6 w-6 grid place-items-center rounded-full cursor-pointer"
                style={{ background: 'rgba(0,0,0,0.7)', color: 'white' }}
              >
                <Trash className="h-3 w-3" />
              </button>
            </div>
          ))}
        </div>
      )}

      <div className="flex justify-end">
        <button
          type="button"
          onClick={postReview}
          disabled={btnLoading}
          className="inline-flex items-center gap-2 h-11 px-5 rounded-full text-[13px] font-semibold cursor-pointer transition-opacity hover:opacity-90 disabled:opacity-60"
          style={{ background: 'white', color: '#0a0a0c' }}
        >
          <Send className="h-3.5 w-3.5" />
          {btnLoading ? 'Илгээж байна' : 'Илгээх'}
        </button>
      </div>
    </div>
  )

  const composerSection = (() => {
    if (!canReview) {
      return (
        <div
          className="mt-6 rounded-2xl p-5 sm:p-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4"
          style={{ background: 'rgba(255,255,255,0.035)', border: '1px solid rgba(255,255,255,0.07)' }}
        >
          <div className="flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <div className="text-[14px] font-semibold text-white" style={{ fontFamily: 'Gilroy, Inter, sans-serif' }}>
                Нэвтэрсэн хэрэглэгч үнэлгээ өгөх боломжтой
              </div>
              {publicBasePoints > 0 && (
                <span className="inline-flex items-center gap-1 text-[11px] font-semibold px-2.5 py-0.5 rounded-full whitespace-nowrap tabular-nums" style={{ background: 'rgba(168,85,247,0.18)', color: '#d8b4fe', border: '1px solid rgba(168,85,247,0.3)' }}>
                  <Medal className="h-3.5 w-3.5" />
                  +{formatNumber(publicBasePoints)} оноо
                </span>
              )}
            </div>
            <p className="mt-1 text-[12.5px] leading-relaxed" style={{ color: 'rgba(255,255,255,0.55)' }}>
              {publicBasePoints > 0
                ? `Нэвтэрч сэтгэгдэл, үнэлгээ үлдээгээд ${formatNumber(publicBasePoints)} оноо цуглуулж, бусад хэрэглэгчдэд зөв сонголт хийхэд нь туслаарай.`
                : 'Хэрэглэсэн бүтээгдэхүүнийхээ талаар сэтгэгдэл, үнэлгээ үлдээж бусад хэрэглэгчдэд зөв сонголт хийхэд нь туслаарай.'}
            </p>
          </div>
          <a
            href={navigateUrl(`/login?url=/product/${product.customId}`, pathname)}
            className="inline-flex items-center gap-2 h-11 px-5 rounded-full text-[13px] font-medium cursor-pointer transition-colors shrink-0"
            style={{ background: 'transparent', color: 'white', border: '1px solid rgba(255,255,255,0.2)' }}
          >
            <LogIn className="h-3.5 w-3.5" />
            Нэвтрэх
          </a>
        </div>
      )
    }

    if (hasAnyReview) {
      return (
        <div
          className="mt-6 rounded-2xl p-5 sm:p-6"
          style={{ background: 'rgba(255,255,255,0.035)', border: '1px solid rgba(255,255,255,0.07)' }}
        >
          <UserReviewStatus
            publishedReview={publishedReview}
            pendingReview={pendingReview}
            deletedReview={deletedReview}
            composerOpen={composerOpen}
            setComposerOpen={setComposerOpen}
            composer={composer}
          />
        </div>
      )
    }

    if (composerOpen) {
      return (
        <div
          className="mt-6 rounded-2xl p-5 sm:p-6"
          style={{ background: 'rgba(255,255,255,0.035)', border: '1px solid rgba(255,255,255,0.07)' }}
        >
          <div className="flex items-center justify-between mb-4 gap-3">
            <div className="flex items-center gap-2 flex-wrap">
              <div className="text-[15px] font-semibold text-white" style={{ fontFamily: 'Gilroy, Inter, sans-serif' }}>
                Сэтгэгдэл бичих
              </div>
              {reviewPointsChip}
            </div>
            <button
              type="button"
              onClick={() => setComposerOpen(false)}
              className="h-8 w-8 grid place-items-center rounded-full cursor-pointer transition-colors"
              style={{ color: 'rgba(255,255,255,0.5)' }}
            >
              <X className="h-4 w-4" />
            </button>
          </div>
          {composer}
        </div>
      )
    }

    return (
      <div
        className="mt-6 rounded-2xl p-5 sm:p-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4"
        style={{ background: 'rgba(255,255,255,0.035)', border: '1px solid rgba(255,255,255,0.07)' }}
      >
        <div className="flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <div className="text-[14px] font-semibold text-white" style={{ fontFamily: 'Gilroy, Inter, sans-serif' }}>
              Энэ бүтээгдэхүүний талаар сэтгэгдэл үлдээх үү?
            </div>
            {reviewPointsChip}
          </div>
          <p className="mt-1 text-[12.5px] leading-relaxed" style={{ color: 'rgba(255,255,255,0.55)' }}>
            {reviewRewardCapped
              ? `Та ${reviewRewardCapWindowDays} хоногт ${reviewRewardCapCount} сэтгэгдэл бичиж оноо цуглуулах лимитэд хүрсэн байна. Таны энэ сэтгэгдэлд оноо олгогдохгүй ч бусад худалдан авагчдад худалдан авалт хийхэд нь туслаарай.`
              : reviewRewardPoints > 0
                ? `Үнэлгээ өгч бусад худалдан авагчдад туслаад, ${formatNumber(reviewRewardPoints)} оноо цуглуулаарай.`
                : 'Хэрэглэсэн бүтээгдэхүүнийхээ талаар үнэлгээ өгч, бусад худалдан авагчдад туслаарай.'}
          </p>
        </div>
        <button
          type="button"
          onClick={() => setComposerOpen(true)}
          className="inline-flex items-center gap-2 h-11 px-5 rounded-full text-[13px] font-semibold cursor-pointer transition-opacity hover:opacity-90 shrink-0"
          style={{ background: 'white', color: '#0a0a0c' }}
        >
          <PenLine className="h-3.5 w-3.5" />
          Сэтгэгдэл бичих
        </button>
      </div>
    )
  })()

  return (
    <div className="cont pt-12 pb-8" style={{ borderTop: '1px solid rgba(255,255,255,0.07)' }}>
      <div className="flex items-end justify-between gap-4 flex-wrap mb-8">
        <div>
          <div className="text-[10px] font-semibold uppercase tracking-[0.22em] mb-2" style={{ color: '#a8a8b3' }}>Сэтгэгдэл</div>
          <h2 className="text-[24px] sm:text-[28px] font-semibold tracking-[-0.02em] leading-none text-white" style={{ fontFamily: 'Gilroy, Inter, sans-serif' }}>
            {totalCount > 0 ? `${totalCount} хэрэглэгчийн үнэлгээ` : 'Сэтгэгдэл байхгүй'}
          </h2>
        </div>
      </div>

      <div className="grid gap-8 lg:gap-10 grid-cols-1 lg:grid-cols-[300px_1fr] items-start">
        <aside className="rounded-2xl p-6" style={{ background: 'rgba(255,255,255,0.035)', border: '1px solid rgba(255,255,255,0.07)' }}>
          <div className="text-center pb-6" style={{ borderBottom: '1px solid rgba(255,255,255,0.07)' }}>
            <div className="font-semibold tabular-nums text-white leading-none" style={{ fontFamily: 'Gilroy, Inter, sans-serif', fontSize: 56 }}>
              {average.toFixed(1)}
            </div>
            <div className="mt-3 inline-flex justify-center"><Stars value={average} size={16} /></div>
            <div className="mt-2 text-[11px]" style={{ color: 'rgba(255,255,255,0.55)' }}>
              {totalCount > 0 ? `${totalCount} сэтгэгдэл дээр үндэслэв` : 'Үнэлгээ хүлээж байна'}
            </div>
          </div>
          <ul className="mt-5 space-y-2.5">
            {breakdown.map(b => (
              <li key={b.stars} className="flex items-center gap-3 text-[11.5px]" style={{ color: 'rgba(255,255,255,0.55)' }}>
                <span className="inline-flex items-center gap-0.5 w-12 tabular-nums">
                  {b.stars}
                  <Star width={11} height={11} fill="currentColor" stroke="none" style={{ color: '#fcd34d' }} />
                </span>
                <div className="flex-1 h-1.5 rounded-full overflow-hidden" style={{ background: 'rgba(255,255,255,0.06)' }}>
                  <div className="h-full transition-all" style={{ width: `${b.pct}%`, background: 'white' }} />
                </div>
                <span className="w-9 text-right tabular-nums">{b.pct}%</span>
              </li>
            ))}
          </ul>
        </aside>

        <div className="space-y-4">
          {visible.length > 0 ? (
            visible.map((r: any) => {
              const customer = r?.customer
              const name = customer?.firstName || customer?.name || r?.fullName || r?.name || 'Хэрэглэгч'
              const when = r?.createdAt ? formatDateNormal(new Date(r.createdAt)) : ''
              const ratingVal = r?.rating || 0
              const comment = r?.comment || r?.body || r?.content || r?.text || ''
              const reviewImages: any[] = Array.isArray(r?.images) ? r.images : []
              return (
                <article key={r.id || `${name}-${when}`} className="rounded-2xl p-6" style={{ background: 'rgba(255,255,255,0.035)', border: '1px solid rgba(255,255,255,0.07)' }}>
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex items-center gap-3 min-w-0">
                      {customer?.avatar ? (
                        <img src={formatImageURL(customer.avatar)} alt={name} className="w-9 h-9 rounded-full object-cover shrink-0" />
                      ) : (
                        <div className="shrink-0">
                          <Avatar size={36} variant="marble" name={customer?.id || name} />
                        </div>
                      )}
                      <div className="min-w-0">
                        <div className="text-[13.5px] font-medium text-white truncate">{name}</div>
                        {when && <div className="text-[11px] tabular-nums" style={{ color: 'rgba(255,255,255,0.55)' }}>{when}</div>}
                      </div>
                    </div>
                    <Stars value={ratingVal} size={13} />
                  </div>
                  {comment && (
                    <div
                      className="mt-3 text-[13.5px] leading-7 prose prose-invert prose-p:mt-0 prose-p:mb-0"
                      style={{ color: 'rgba(255,255,255,0.78)' }}
                      dangerouslySetInnerHTML={{ __html: comment }}
                    />
                  )}
                  {reviewImages.length > 0 && (
                    <div className="mt-3 flex flex-wrap gap-2">
                      {reviewImages.map((image: any, idx: number) => (
                        <img key={idx} src={formatImageURL(image)} alt={`Review image ${idx + 1}`} className="w-16 h-16 object-cover rounded-md" style={{ border: '1px solid rgba(255,255,255,0.07)' }} />
                      ))}
                    </div>
                  )}
                </article>
              )
            })
          ) : (
            <div
              className="rounded-2xl p-10 text-center"
              style={{ background: 'rgba(255,255,255,0.035)', border: '1px solid rgba(255,255,255,0.07)' }}
            >
              <div className="text-[14px] font-medium text-white mb-2">Сэтгэгдэл хоосон байна</div>
              <div className="text-[12.5px]" style={{ color: 'rgba(255,255,255,0.55)' }}>
                Энэ бүтээгдэхүүнд хараахан сэтгэгдэл байхгүй байна.
              </div>
            </div>
          )}

          {hasMore && (
            <button
              type="button"
              onClick={() => setShowAll(s => !s)}
              className="w-full h-11 rounded-full text-[12.5px] font-medium cursor-pointer transition-colors hover:opacity-90"
              style={{ background: 'transparent', color: 'white', border: '1px solid rgba(255,255,255,0.2)' }}
            >
              {showAll ? 'Хураах' : `Бүх сэтгэгдэл үзэх (+${reviewItems.length - 3})`}
            </button>
          )}
        </div>
      </div>

      {composerSection}
    </div>
  )
}

function UserReviewStatus({ publishedReview, pendingReview, deletedReview, composerOpen, setComposerOpen, composer }: { publishedReview: any, pendingReview: any, deletedReview: any, composerOpen: boolean, setComposerOpen: (v: boolean) => void, composer: React.ReactNode }) {
  return (
    <div className="space-y-4">
      {publishedReview && (
        <StatusRow
          icon={CircleCheckBig}
          iconColor="#34d399"
          iconBg="rgba(16,185,129,0.16)"
          iconBorder="rgba(16,185,129,0.3)"
          title="Та амжилттай үнэлгээ өгсөн байна"
          badge={{ label: 'Нийтлэгдсэн', color: '#34d399', bg: 'rgba(16,185,129,0.16)' }}
          rating={publishedReview.rating}
          createdAt={publishedReview.createdAt}
          comment={publishedReview.comment}
        />
      )}
      {pendingReview && (
        <StatusRow
          icon={CircleHelp}
          iconColor="#fcd34d"
          iconBg="rgba(245,158,11,0.16)"
          iconBorder="rgba(245,158,11,0.3)"
          title="Таны үнэлгээ хянагдаж байна"
          badge={{ label: 'Хянагдаж байна', color: '#fcd34d', bg: 'rgba(245,158,11,0.16)' }}
          rating={pendingReview.rating}
          createdAt={pendingReview.createdAt}
          comment={pendingReview.comment}
        />
      )}
      {deletedReview && !publishedReview && (
        <div>
          <StatusRow
            icon={CircleX}
            iconColor="#fda4af"
            iconBg="rgba(244,63,94,0.16)"
            iconBorder="rgba(244,63,94,0.3)"
            title="Таны үнэлгээг татгалзсан байна"
            badge={{ label: 'Татгалзсан', color: '#fda4af', bg: 'rgba(244,63,94,0.16)' }}
            footer={
              <p className="text-[11.5px] mt-1 leading-snug" style={{ color: 'rgba(255,255,255,0.55)' }}>
                {pendingReview ? 'Засварлагдсан сэтгэгдлийг хянаж байна.' : 'Та сэтгэгдлээ дахин засварлаж илгээх боломжтой.'}
                {deletedReview.createdAt && (
                  <span className="ml-1.5 tabular-nums">· {formatDateNormal(new Date(deletedReview.createdAt))}</span>
                )}
              </p>
            }
          />
          {!pendingReview && !publishedReview && (
            composerOpen ? (
              <div className="mt-4 pt-4" style={{ borderTop: '1px solid rgba(255,255,255,0.07)' }}>
                {composer}
              </div>
            ) : (
              <div className="mt-3 flex justify-end">
                <button
                  type="button"
                  onClick={() => setComposerOpen(true)}
                  className="inline-flex items-center gap-2 h-10 px-4 rounded-full text-[12.5px] font-medium cursor-pointer transition-colors"
                  style={{ background: 'transparent', color: 'white', border: '1px solid rgba(255,255,255,0.2)' }}
                >
                  <PenLine className="h-3.5 w-3.5" />
                  Дахин үнэлгээ өгөх
                </button>
              </div>
            )
          )}
        </div>
      )}
    </div>
  )
}

function StatusRow({ icon: Icon, iconColor, iconBg, iconBorder, title, badge, rating, createdAt, comment, footer }: { icon: any, iconColor: string, iconBg: string, iconBorder: string, title: string, badge: { label: string, color: string, bg: string }, rating?: number, createdAt?: string, comment?: string, footer?: React.ReactNode }) {
  return (
    <div className="flex items-start gap-3">
      <div
        className="h-9 w-9 rounded-full grid place-items-center shrink-0"
        style={{ background: iconBg, border: `1px solid ${iconBorder}`, color: iconColor }}
      >
        <Icon className="h-4 w-4" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="text-[13.5px] font-semibold text-white leading-tight">{title}</div>
          <span
            className="inline-flex items-center text-[10.5px] font-medium px-2 py-0.5 rounded-full uppercase tracking-[0.05em]"
            style={{ background: badge.bg, color: badge.color, border: `1px solid ${badge.color}40` }}
          >
            {badge.label}
          </span>
        </div>
        {(rating !== undefined || createdAt) && (
          <div className="mt-1.5 flex items-center gap-2">
            {rating !== undefined && (
              <span className="inline-flex items-center gap-0.5" style={{ color: '#fcd34d' }}>
                {[1, 2, 3, 4, 5].map(i => (
                  <Star key={i} width={13} height={13} fill={i <= rating ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth={1.5} />
                ))}
              </span>
            )}
            {createdAt && (
              <span className="text-[11px] tabular-nums" style={{ color: 'rgba(255,255,255,0.55)' }}>
                · {formatDateNormal(new Date(createdAt))}
              </span>
            )}
          </div>
        )}
        {comment && (
          <div
            className="mt-2 text-[13px] leading-relaxed prose prose-invert prose-p:mt-0 prose-p:mb-0"
            style={{ color: 'rgba(255,255,255,0.78)' }}
            dangerouslySetInnerHTML={{ __html: comment }}
          />
        )}
        {footer}
      </div>
    </div>
  )
}
